import jsQR from 'jsqr';
import { Html5Qrcode } from 'html5-qrcode';

export class QRScanner {
  constructor(containerId, onScanSuccess, onScanError) {
    this.containerId = containerId;
    this.onScanSuccess = onScanSuccess;
    this.onScanError = onScanError;
    this.isScanning = false;
    this.stream = null;
    this.videoEl = null;
    this.canvasEl = null;
    this.ctx = null;
    this.animId = null;
    this.zoom = 1.8; // Default 1.8x zoom for optimal receipt scanning distance
    this.isTorchOn = false;
    this.frameCount = 0;
  }

  async start() {
    if (this.isScanning) {
      this.stop();
    }

    const container = document.getElementById(this.containerId);
    if (!container) throw new Error(`Container #${this.containerId} not found`);

    // Setup Video and Canvas Elements inside container
    container.innerHTML = '';
    container.className = 'scanner-video-container';

    this.videoEl = document.createElement('video');
    this.videoEl.setAttribute('autoplay', 'true');
    this.videoEl.setAttribute('muted', 'true');
    this.videoEl.setAttribute('playsinline', 'true');
    this.videoEl.setAttribute('webkit-playsinline', 'true');
    this.videoEl.setAttribute('disablePictureInPicture', 'true');
    this.videoEl.setAttribute('disableRemotePlayback', 'true');
    this.videoEl.muted = true;
    this.videoEl.autoplay = true;
    this.videoEl.playsInline = true;
    this.videoEl.style.width = '100%';
    this.videoEl.style.height = '100%';
    this.videoEl.style.objectFit = 'cover';
    this.videoEl.style.transform = `scale(${this.zoom})`;
    container.appendChild(this.videoEl);

    // Overlay visual viewfinder
    const overlay = document.createElement('div');
    overlay.className = 'scan-overlay';
    overlay.innerHTML = `
      <div class="scan-frame">
        <div class="corner-bl"></div>
        <div class="corner-br"></div>
        <div class="scan-line"></div>
      </div>
    `;
    container.appendChild(overlay);

    // Offscreen Canvas for processing
    this.canvasEl = document.createElement('canvas');
    this.ctx = this.canvasEl.getContext('2d', { willReadFrequently: true });

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error('Navegador sem suporte à câmera.');
    }

    // Request stream with progressive mobile fallbacks
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } }
      });
    } catch (err) {
      console.warn('Ideal camera constraint failed, trying environment facingMode:', err);
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: 'environment' }
        });
      } catch (e2) {
        console.warn('Environment camera failed, trying generic video stream:', e2);
        this.stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: true
        });
      }
    }

    this.videoEl.srcObject = this.stream;
    
    // Ensure video is played safely without throwing on initial promise
    const tryPlay = () => {
      if (this.videoEl && this.videoEl.paused) {
        this.videoEl.play().catch(e => console.warn('Video play deferred/handled:', e));
      }
    };

    this.videoEl.onloadedmetadata = tryPlay;
    this.videoEl.oncanplay = tryPlay;
    this.videoEl.onloadeddata = tryPlay;
    tryPlay();

    this.isScanning = true;
    this.startScanLoop();
  }

  setZoom(zoomLevel) {
    this.zoom = Math.max(1, Math.min(zoomLevel, 3.5));
    if (this.videoEl) {
      this.videoEl.style.transform = `scale(${this.zoom})`;
    }
    // Also try hardware zoom if device supports it (Android / modern WebRTC)
    if (this.stream) {
      const track = this.stream.getVideoTracks()[0];
      if (track && track.applyConstraints) {
        track.applyConstraints({
          advanced: [{ zoom: this.zoom }]
        }).catch(() => {});
      }
    }
    return true;
  }

  hasTorch() {
    if (!this.stream) return false;
    const track = this.stream.getVideoTracks()[0];
    if (!track || !track.getCapabilities) return false;
    try {
      const caps = track.getCapabilities();
      return !!caps.torch;
    } catch (e) {
      return false;
    }
  }

  async toggleTorch() {
    if (!this.stream || !this.hasTorch()) return false;
    const track = this.stream.getVideoTracks()[0];
    try {
      this.isTorchOn = !this.isTorchOn;
      await track.applyConstraints({
        advanced: [{ torch: this.isTorchOn }]
      });
      return this.isTorchOn;
    } catch (err) {
      console.warn('Torch toggle error:', err);
      return false;
    }
  }

  startScanLoop() {
    let barcodeDetector = null;
    if ('BarcodeDetector' in window) {
      try {
        barcodeDetector = new window.BarcodeDetector({ formats: ['qr_code'] });
      } catch (e) {}
    }

    const tick = async () => {
      if (!this.isScanning) return;

      if (this.videoEl && (this.videoEl.readyState >= 2 || (this.videoEl.videoWidth > 0 && this.videoEl.videoHeight > 0))) {
        this.frameCount++;
        const vW = this.videoEl.videoWidth;
        const vH = this.videoEl.videoHeight;

        if (vW > 0 && vH > 0) {
          // Dynamic Center Crop based on Zoom Level
          const cropSize = Math.floor(Math.min(vW, vH) / this.zoom);
          const cropX = Math.floor((vW - cropSize) / 2);
          const cropY = Math.floor((vH - cropSize) / 2);

          // Target processing resolution (700x700 for maximum sharp module detection)
          const targetDim = 700;
          this.canvasEl.width = targetDim;
          this.canvasEl.height = targetDim;

          this.ctx.drawImage(this.videoEl, cropX, cropY, cropSize, cropSize, 0, 0, targetDim, targetDim);
          const imgData = this.ctx.getImageData(0, 0, targetDim, targetDim);

          // Pass 1: jsQR on raw high-res center crop
          let code = jsQR(imgData.data, targetDim, targetDim, {
            inversionAttempts: 'dontInvert'
          });

          // Pass 2: Hardware BarcodeDetector if available
          if (!code && barcodeDetector && this.frameCount % 2 === 0) {
            try {
              const barcodes = await barcodeDetector.detect(this.canvasEl);
              if (barcodes && barcodes.length > 0 && barcodes[0].rawValue) {
                code = { data: barcodes[0].rawValue };
              }
            } catch (e) {}
          }

          // Pass 3: Contrast-boosted filter for faint/thermal prints every 3 frames
          if (!code && this.frameCount % 3 === 0) {
            const data = imgData.data;
            // Contrast boost (+35%)
            const contrast = 35;
            const factor = (259 * (contrast + 255)) / (255 * (259 - contrast));
            for (let i = 0; i < data.length; i += 4) {
              data[i] = factor * (data[i] - 128) + 128;
              data[i + 1] = factor * (data[i + 1] - 128) + 128;
              data[i + 2] = factor * (data[i + 2] - 128) + 128;
            }
            code = jsQR(data, targetDim, targetDim, {
              inversionAttempts: 'attemptBoth'
            });
          }

          if (code && code.data && code.data.trim().length > 0) {
            if (navigator.vibrate) navigator.vibrate(120);
            this.stop();
            this.onScanSuccess(code.data.trim());
            return;
          }
        }
      }

      this.animId = requestAnimationFrame(tick);
    };

    this.animId = requestAnimationFrame(tick);
  }

  async scanFile(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = async () => {
        URL.revokeObjectURL(url);

        try {
          // Pass 1: Try Native BarcodeDetector on original image
          if ('BarcodeDetector' in window) {
            try {
              const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
              const barcodes = await detector.detect(img);
              if (barcodes && barcodes.length > 0 && barcodes[0].rawValue) {
                return resolve(barcodes[0].rawValue);
              }
            } catch (e) {}
          }

          // Pass 2: Process with Canvas at 1200px and 1600px with jsQR
          const resolutions = [1400, 1000, 1800];
          for (const targetDim of resolutions) {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            let w = img.width;
            let h = img.height;
            if (w > targetDim || h > targetDim) {
              if (w > h) {
                h = Math.round((h * targetDim) / w);
                w = targetDim;
              } else {
                w = Math.round((w * targetDim) / h);
                h = targetDim;
              }
            }

            canvas.width = w;
            canvas.height = h;
            ctx.drawImage(img, 0, 0, w, h);

            // 2A: Raw image jsQR
            const imgData = ctx.getImageData(0, 0, w, h);
            let code = jsQR(imgData.data, w, h, { inversionAttempts: 'attemptBoth' });
            if (code && code.data) return resolve(code.data);

            // 2B: Contrast boost
            const data = imgData.data;
            const contrast = 40;
            const factor = (259 * (contrast + 255)) / (255 * (259 - contrast));
            for (let i = 0; i < data.length; i += 4) {
              data[i] = factor * (data[i] - 128) + 128;
              data[i + 1] = factor * (data[i + 1] - 128) + 128;
              data[i + 2] = factor * (data[i + 2] - 128) + 128;
            }
            code = jsQR(data, w, h, { inversionAttempts: 'attemptBoth' });
            if (code && code.data) return resolve(code.data);

            // 2C: Grayscale binarization for thermal watermark text suppression
            for (let i = 0; i < data.length; i += 4) {
              const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
              const val = gray < 130 ? 0 : 255;
              data[i] = val;
              data[i + 1] = val;
              data[i + 2] = val;
            }
            code = jsQR(data, w, h, { inversionAttempts: 'attemptBoth' });
            if (code && code.data) return resolve(code.data);
          }

          // Fallback to Html5Qrcode file scan
          const scanner = new Html5Qrcode('qr-reader');
          try {
            const res = await scanner.scanFile(file, true);
            try { scanner.clear(); } catch(e) {}
            if (res) return resolve(res);
          } catch(e) {
            try { scanner.clear(); } catch(e) {}
          }

          reject(new Error('Nenhum QR Code reconhecido'));
        } catch (err) {
          reject(err);
        }
      };

      img.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(e);
      };

      img.src = url;
    });
  }

  stop() {
    this.isScanning = false;

    if (this.animId) {
      cancelAnimationFrame(this.animId);
      this.animId = null;
    }

    if (this.stream) {
      this.stream.getTracks().forEach(track => {
        try { track.stop(); } catch (e) {}
      });
      this.stream = null;
    }

    if (this.videoEl) {
      this.videoEl.srcObject = null;
      this.videoEl = null;
    }
  }
}
